import React from 'react';
import { X, Calendar, Target, TrendingUp, TrendingDown, Users, BarChart3, AlertTriangle, CheckCircle, Clock, FlaskConical } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface ExperimentDetailsModalProps {
  experiment: any;
  isOpen: boolean;
  onClose: () => void;
}

const ExperimentDetailsModal: React.FC<ExperimentDetailsModalProps> = ({ experiment, isOpen, onClose }) => {
  if (!isOpen || !experiment) return null;

  // Mock data for hypothesis validation charts
  const performanceData = [
    { week: 'Week 1', baseline: 100, experiment: 102, target: 125 },
    { week: 'Week 2', baseline: 98, experiment: 108, target: 125 },
    { week: 'Week 3', baseline: 101, experiment: 115, target: 125 },
    { week: 'Week 4', baseline: 99, experiment: 121, target: 125 },
    { week: 'Week 5', baseline: 102, experiment: 118, target: 125 },
    { week: 'Week 6', baseline: 100, experiment: 123, target: 125 },
  ];

  const segmentData = [
    { segment: 'Mobile Users', performance: 128, sample: 2400 },
    { segment: 'Desktop Users', performance: 118, sample: 1800 },
    { segment: 'New Users', performance: 135, sample: 1200 },
    { segment: 'Returning Users', performance: 115, sample: 3600 },
  ];

  const confidenceData = [
    { name: 'Statistical Significance', value: 95, color: '#10B981' },
    { name: 'Sample Size Adequacy', value: 88, color: '#3B82F6' },
    { name: 'Effect Size', value: 82, color: '#8B5CF6' },
    { name: 'External Validity', value: 76, color: '#F59E0B' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'proposed': return 'bg-blue-900 text-blue-300 border-blue-600';
      case 'running': return 'bg-yellow-900 text-yellow-300 border-yellow-600';
      case 'completed': return 'bg-green-900 text-green-300 border-green-600';
      case 'failed': return 'bg-red-900 text-red-300 border-red-600';
      default: return 'bg-gray-900 text-gray-300 border-gray-600';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'proposed': return <Clock className="w-5 h-5" />;
      case 'running': return <FlaskConical className="w-5 h-5" />;
      case 'completed': return <CheckCircle className="w-5 h-5" />;
      case 'failed': return <AlertTriangle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const calculateDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = calculateDaysRemaining(experiment.expectedEnd);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-900 rounded-t-lg">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${getStatusColor(experiment.status)}`}>
              {getStatusIcon(experiment.status)}
              <span className="capitalize font-medium">{experiment.status}</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{experiment.title}</h2>
              <p className="text-gray-400">Experiment ID: {experiment.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colours p-2 rounded-lg hover:bg-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Overview Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Experiment Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Hypothesis */}
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                  <Target className="w-5 h-5 text-indigo-400" />
                  <span>Hypothesis</span>
                </h3>
                <p className="text-gray-300 leading-relaxed">{experiment.hypothesis}</p>
              </div>

              {/* Timeline */}
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-green-400" />
                  <span>Timeline</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-400">Start Date</div>
                    <div className="text-white font-medium">{formatDate(experiment.startDate)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Expected End</div>
                    <div className="text-white font-medium">{formatDate(experiment.expectedEnd)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Days Remaining</div>
                    <div className={`font-medium ${daysRemaining > 7 ? 'text-green-400' : daysRemaining > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {daysRemaining > 0 ? `${daysRemaining} days` : 'Overdue'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Results (if available) */}
              {experiment.results && (
                <div className="bg-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                    <span>Results Summary</span>
                  </h3>
                  <p className="text-gray-300 leading-relaxed">{experiment.results}</p>
                </div>
              )}
            </div>

            {/* Key Metrics */}
            <div className="space-y-4">
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Key Metrics</h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-sm text-gray-400">Confidence Level</div>
                    <div className="text-3xl font-bold text-white">{experiment.confidence}%</div>
                    <div className={`text-sm ${experiment.confidence >= 80 ? 'text-green-400' : experiment.confidence >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {experiment.confidence >= 80 ? 'High Confidence' : experiment.confidence >= 60 ? 'Medium Confidence' : 'Low Confidence'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-gray-400">Impact Level</div>
                    <div className={`text-2xl font-bold capitalize ${getImpactColor(experiment.impact)}`}>
                      {experiment.impact}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-gray-400">Sample Size</div>
                    <div className="text-xl font-bold text-white">8,400</div>
                    <div className="text-sm text-gray-400">users</div>
                  </div>
                </div>
              </div>

              {/* Signal Tags */}
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Signal Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {experiment.signals.map((signal: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-indigo-900 text-indigo-300 rounded-full text-sm"
                    >
                      {signal}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Hypothesis Validation Charts */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center space-x-2">
              <TrendingUp className="w-6 h-6 text-indigo-400" />
              <span>Hypothesis Validation Analysis</span>
            </h3>

            {/* Performance Trend Chart */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Performance vs Baseline</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="week" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="baseline" 
                    stroke="#6B7280" 
                    strokeWidth={2}
                    name="Baseline"
                    strokeDasharray="5 5"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="experiment" 
                    stroke="#4F46E5" 
                    strokeWidth={3}
                    name="Experiment"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    name="Target"
                    strokeDasharray="10 5"
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm text-gray-400">Current Performance</div>
                  <div className="text-xl font-bold text-indigo-400">123%</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">vs Baseline</div>
                  <div className="text-xl font-bold text-green-400">+23%</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">Target Achievement</div>
                  <div className="text-xl font-bold text-yellow-400">98%</div>
                </div>
              </div>
            </div>

            {/* Segment Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-white mb-4">Performance by Segment</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={segmentData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="segment" stroke="#9CA3AF" angle={-45} textAnchor="end" height={80} />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                    <Bar dataKey="performance" fill="#4F46E5" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Confidence Breakdown */}
              <div className="bg-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-white mb-4">Confidence Breakdown</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={confidenceData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                      labelLine={false}
                    >
                      {confidenceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Statistical Analysis */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Statistical Analysis</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-sm text-gray-400">P-Value</div>
                  <div className="text-2xl font-bold text-green-400">0.003</div>
                  <div className="text-xs text-green-400">Statistically Significant</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-400">Effect Size</div>
                  <div className="text-2xl font-bold text-indigo-400">0.82</div>
                  <div className="text-xs text-indigo-400">Large Effect</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-400">Power</div>
                  <div className="text-2xl font-bold text-purple-400">95%</div>
                  <div className="text-xs text-purple-400">Well Powered</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-400">Conversion Lift</div>
                  <div className="text-2xl font-bold text-yellow-400">+18.5%</div>
                  <div className="text-xs text-yellow-400">Above Target</div>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Recommendations</span>
              </h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="text-white font-medium">Scale to Full Population</div>
                    <div className="text-gray-400 text-sm">Results show consistent positive impact across all segments with high statistical confidence.</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="text-white font-medium">Monitor Mobile Performance</div>
                    <div className="text-gray-400 text-sm">Mobile users show highest engagement lift - consider mobile-specific optimisations.</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="text-white font-medium">Prepare Rollout Plan</div>
                    <div className="text-gray-400 text-sm">Develop phased rollout strategy to minimise risk whilst capturing benefits.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0 pt-6 border-t border-gray-700">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
              <span>Last updated: 2 hours ago</span>
              <span>•</span>
              <span>Next review: {formatDate(experiment.expectedEnd)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colours">
                Export Data
              </button>
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colours">
                Update Status
              </button>
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colours">
                Scale Experiment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperimentDetailsModal;